// eslint-disable-next-line no-underscore-dangle
export const getBasePath = (): string => window.__RUNTIME_CONFIG__?.BASE_PATH;

// eslint-disable-next-line no-underscore-dangle
export const getAuthMode = (): string => window.__RUNTIME_CONFIG__?.AUTH_MODE;

// eslint-disable-next-line no-underscore-dangle
export const getAuthMsEntraClientId = (): string => window.__RUNTIME_CONFIG__?.AUTH_MS_ENTRA_CLIENT_ID;

// eslint-disable-next-line no-underscore-dangle
export const getAuthMsEntraTenantId = (): string => window.__RUNTIME_CONFIG__?.AUTH_MS_ENTRA_TENANT_ID;

// eslint-disable-next-line no-underscore-dangle
export const getAuthMsEntraRedirectUrl = (): string => window.__RUNTIME_CONFIG__?.AUTH_MS_ENTRA_REDIRECT_URL;

// eslint-disable-next-line no-underscore-dangle
export const getAuthTokenRefreshInterval = (): number => parseInt(window.__RUNTIME_CONFIG__?.AUTH_TOKEN_REFRESH_INTERVAL, 10);

// eslint-disable-next-line no-underscore-dangle
export const getDefaultApp = (): string => window.__RUNTIME_CONFIG__?.DEFAULT_APP;

// eslint-disable-next-line no-underscore-dangle
export const getAuthApi = (): string => window.__RUNTIME_CONFIG__?.AUTH_API;

// eslint-disable-next-line no-underscore-dangle
export const getTmsAppRemoteEntrypoint = (): string => window.__RUNTIME_CONFIG__?.TMS_APP_REMOTE_ENTRYPOINT;

// eslint-disable-next-line no-underscore-dangle
export const getPmgAppRemoteEntrypoint = (): string => window.__RUNTIME_CONFIG__?.PMG_APP_REMOTE_ENTRYPOINT;

// eslint-disable-next-line no-underscore-dangle
export const getDmsAppRemoteEntrypoint = (): string => window.__RUNTIME_CONFIG__?.DMS_APP_REMOTE_ENTRYPOINT;

// eslint-disable-next-line no-underscore-dangle
export const getProvisioningPortalAppRemoteEntrypoint = (): string => window.__RUNTIME_CONFIG__?.PROVISIONING_PORTAL_APP_REMOTE_ENTRYPOINT;

// eslint-disable-next-line no-underscore-dangle
export const getAuthAppRemoteEntrypoint = (): string => window.__RUNTIME_CONFIG__?.AUTH_APP_REMOTE_ENTRYPOINT;

export type SupportedAppListener = () => void;

export class SupportedApp {
    configuration?: unknown;

    private loaded = false;

    private listeners: SupportedAppListener[] = [];

    constructor(readonly name: string, readonly title: string, readonly enabled: boolean, readonly runtimeEnv: string) {}

    // The class is persisted via Redux, fetched instance is not referentially
    // equal to any of the predefined values, hence the need for an equality comparer
    equals(other: SupportedApp): boolean {
        return this.name === other.name;
    }

    static isAppEnabled(remoteEndpoint: string): boolean {
        const unconfigured = "none.js";

        return !!remoteEndpoint && remoteEndpoint !== unconfigured;
    }

    static fromRemoteEntryPoint(name: string, title: string, remoteEntryPoint: string): SupportedApp {
        const enabled = SupportedApp.isAppEnabled(remoteEntryPoint);
        const runtimeEnv = enabled ? remoteEntryPoint.replace("remoteEntry.js", "runtime-env.js") : "n/a";

        return new SupportedApp(name, title, enabled, runtimeEnv);
    }

    loadConfiguration(): void {
        if (!this.enabled) {
            return;
        }

        fetch(this.runtimeEnv)
            .then((resp) => resp.text())
            .then((body) => {
                const firstLeftBrace = body.indexOf("{");
                const lastRightBrace = body.indexOf("}");
                if (firstLeftBrace < 0 || lastRightBrace < 0) {
                    // eslint-disable-next-line no-console
                    console.error(`Data from ${this.runtimeEnv}: `, body);
                    throw new Error(`Unable to parse configuration of application '${this.name}' from endpoint ${this.runtimeEnv}`);
                }

                this.configuration = JSON.parse(body.substring(firstLeftBrace, lastRightBrace + 1));
                this.loaded = true;
                this.notifyListeners();
            })
            .catch((e) => {
                // eslint-disable-next-line no-console
                console.error(`Error fetching configuration for application '${this.name}' from endpoint ${this.runtimeEnv}: `, e);
                this.configuration = undefined;
                this.loaded = false;
                this.notifyListeners();
            });
    }

    subscribe(listener: SupportedAppListener) {
        this.listeners.push(listener);
    }

    unsubscribe(listener: SupportedAppListener) {
        this.listeners = this.listeners.filter((l) => l !== listener);
    }

    isLoaded(): boolean {
        return this.loaded;
    }

    private notifyListeners() {
        // Notify listeners asynchronously to avoid React state update issues
        queueMicrotask(() => {
            this.listeners.forEach((listener) => listener());
        });
    }
}

export const SupportedApps = {
    tms: SupportedApp.fromRemoteEntryPoint("TMS", "TMS", getTmsAppRemoteEntrypoint()),
    pmg: SupportedApp.fromRemoteEntryPoint("PMG", "TMS - PMG", getPmgAppRemoteEntrypoint()),
    dms: SupportedApp.fromRemoteEntryPoint("DMS", "DMS", getDmsAppRemoteEntrypoint()),
    provisioningPortal: SupportedApp.fromRemoteEntryPoint(
        "ProvisioningPortal",
        "Provisioning Portal",
        getProvisioningPortalAppRemoteEntrypoint(),
    ),
    authApp: SupportedApp.fromRemoteEntryPoint("AuthApp", "Auth App", getAuthAppRemoteEntrypoint()),
    dummy: new SupportedApp("Dummy", "Dummy app 1", false, "n/a"),
};

const AllSupportedApps: SupportedApp[] = Object.keys(SupportedApps).map((name: string) => SupportedApps[name] as SupportedApp);

export const AppRouteMap = new Map(Object.values(SupportedApps).map((app) => [app.name, app]));

export const DefaultApp = (() => {
    if (getDefaultApp()) {
        const supportedApp = AllSupportedApps.find((app) => app.name === getDefaultApp());
        if (supportedApp === undefined)
            // eslint-disable-next-line no-console
            console.warn(`Default app configured to an unsupported app, ignoring: ${getDefaultApp()}`);
        else return supportedApp;
    }
    return SupportedApps.tms;
})();

export const getActiveApps: () => SupportedApp[] = () => AllSupportedApps.filter((app) => app.enabled && app.isLoaded());
